import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, Compass, GraduationCap, TrendingUp } from "lucide-react";
import { PageHero, SectionLabel } from "@/components/Bits";
import Seo from "@/components/Seo";
import { Reveal, RevealGroup, RevealItem } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { IMAGES } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field =
  "rounded-none border-line bg-white focus-visible:ring-1 focus-visible:ring-navy focus-visible:border-navy";

const PERKS = [
  { icon: Compass, t: "Systems, not guesswork", d: "You execute a proven, documented process — set up to win from day one." },
  { icon: GraduationCap, t: "Real development", d: "Structured onboarding, coaching, and a clear leadership track." },
  { icon: TrendingUp, t: "Merit-based growth", d: "Performance standards that reward discipline and consistency." },
];

export default function Careers() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", location: "", experience: "", linkedin: "",
    role: "Pipeline Development Specialist", message: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/careers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      toast.success("Application received — thank you for your interest.");
      setForm({ name: "", email: "", phone: "", location: "", experience: "", linkedin: "", role: "Pipeline Development Specialist", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="careers-page">
      <Seo
        title="Careers"
        description="Become a Pipeline Development Specialist at CCA. Join a disciplined, systems-driven commercial insurance pipeline development team with a clear leadership track."
        path="/careers"
      />
      <PageHero
        eyebrow="Careers"
        title="Become a Pipeline Development Specialist."
        intro="We're building the next generation of disciplined commercial pipeline professionals. If you value systems, standards, and growth, we want to meet you."
      />

      <section className="border-b border-line bg-paper py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Reveal>
            <SectionLabel num="/">Why build your career here</SectionLabel>
          </Reveal>
          <RevealGroup className="mt-12 grid grid-cols-1 gap-px bg-line md:grid-cols-3">
            {PERKS.map((p) => (
              <RevealItem key={p.t} className="bg-paper p-8 md:p-10">
                <p.icon className="h-6 w-6 text-navy" strokeWidth={1.5} />
                <h3 className="mt-5 font-display text-xl font-medium tracking-tight text-ink">{p.t}</h3>
                <p className="mt-3 leading-relaxed text-subtext">{p.d}</p>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      <section className="bg-paper py-20 md:py-28">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-14 px-6 md:grid-cols-12 md:px-12 lg:px-16">
          <Reveal className="md:col-span-5">
            <div className="md:sticky md:top-28">
              <div className="overflow-hidden border border-line">
                <img src={IMAGES.execPortrait} alt="A CCA specialist" className="aspect-[4/5] w-full object-cover" />
              </div>
              <div className="mt-8 border border-line p-6">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-clay">Open Role</p>
                <h3 className="mt-3 font-display text-2xl font-medium tracking-tight text-ink">
                  Pipeline Development Specialist
                </h3>
                <p className="mt-3 leading-relaxed text-subtext">
                  Execute standardized outreach systems on behalf of commercial agencies, held to a
                  clear quality bar, with a defined path into leadership.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal className="md:col-span-7" delay={0.1}>
            <form onSubmit={submit} data-testid="careers-form" className="border border-line bg-white p-8 md:p-10">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-subtext">Apply now</p>
              <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="k-name">Full name *</Label>
                  <Input id="k-name" data-testid="career-name" className={field} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="k-email">Email *</Label>
                  <Input id="k-email" type="email" data-testid="career-email" className={field} value={form.email} onChange={set("email")} placeholder="jane@email.com" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="k-phone">Phone</Label>
                  <Input id="k-phone" data-testid="career-phone" className={field} value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="k-loc">Location</Label>
                  <Input id="k-loc" data-testid="career-location" className={field} value={form.location} onChange={set("location")} placeholder="City, State" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="k-exp">Years of experience</Label>
                  <Input id="k-exp" data-testid="career-experience" className={field} value={form.experience} onChange={set("experience")} placeholder="e.g. 3 years in sales" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="k-li">LinkedIn</Label>
                  <Input id="k-li" data-testid="career-linkedin" className={field} value={form.linkedin} onChange={set("linkedin")} placeholder="linkedin.com/in/…" />
                </div>
              </div>
              <div className="mt-6 space-y-2">
                <Label htmlFor="k-msg">Why CCA?</Label>
                <Textarea id="k-msg" data-testid="career-message" rows={5} className={field} value={form.message} onChange={set("message")} placeholder="Tell us why disciplined pipeline development appeals to you." />
              </div>
              <button
                type="submit"
                data-testid="career-submit"
                disabled={loading}
                className="group mt-8 inline-flex items-center gap-2 border border-ink bg-ink px-7 py-3.5 font-mono text-[12px] uppercase tracking-[0.12em] text-paper transition-colors hover:bg-transparent hover:text-ink disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {loading ? "Submitting" : "Submit Application"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>

    </div>
  );
}
